import { Box, Popover, Stack, Text } from '@mantine/core';
import { IconHistory, IconChevronRight } from '@tabler/icons-react';
import { useState } from 'react';
import { useRecentlyViewedClients } from '../contexts/RecentlyViewedClientsContext';
import { UnifyNavLink } from './UnifyNavLink';

interface RecentlyViewedNavSectionProps {
  onNavigate: (href: string) => void;
}

/**
 * Left-navbar "Recently Viewed" item for practitioner-backed roles.
 * Renders as a normal flat sidebar NavLink (same styling as the other main
 * nav items). Clicking it opens a floating Popover card — matching the
 * NotificationsPopover convention — listing up to the last 4 client profiles
 * viewed by the current practitioner on this browser, sourced from a
 * practitioner-scoped cookie.
 *
 * No data is fetched here on scroll/re-render — `recentlyViewed`/`loading` come
 * from the shared RecentlyViewedClientsProvider, which only reloads on mount and
 * after a write. `hideDetached={false}` below disables Mantine's default
 * behavior of setting `display: none` on the dropdown whenever its trigger
 * scrolls outside the navbar's visible scroll area — that toggling (not any
 * data refresh) was the source of the flicker while scrolling.
 */
export function RecentlyViewedNavSection({ onNavigate }: RecentlyViewedNavSectionProps): JSX.Element {
  const { recentlyViewed, loading } = useRecentlyViewedClients();
  const [opened, setOpened] = useState(false);

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      closeOnClickOutside
      closeOnEscape
      hideDetached={false}
      position="right-start"
      width={260}
      shadow="lg"
      radius="md"
      offset={8}
      withinPortal
    >
      <Popover.Target>
        <Box>
          <UnifyNavLink
            label="Recently Viewed"
            icon={<IconHistory size={20} stroke={1.5} />}
            rightSection={<IconChevronRight size={16} />}
            active={opened}
            onNavigate={() => setOpened((o) => !o)}
          />
        </Box>
      </Popover.Target>

      <Popover.Dropdown p={0}>
        <Stack gap={0} py={4} px={4}>
          {recentlyViewed.length > 0 ? (
            recentlyViewed.map((c) => (
              <UnifyNavLink
                key={c.patientReference}
                label={c.displayName}
                href={`/dashboard/clients/${c.patientId}`}
                onNavigate={() => {
                  setOpened(false);
                  onNavigate(`/dashboard/clients/${c.patientId}`);
                }}
              />
            ))
          ) : (
            <Text size="xs" c="dimmed" px="sm" py={8}>
              {loading ? 'Loading…' : 'No recently viewed clients'}
            </Text>
          )}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
